import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { TaskyButton } from '../button';

/** 強調枠を要素の外側へ広げる余白（px） */
const HIGHLIGHT_PADDING = 6;
/** 画面の暗さ。強調枠の外側を box-shadow で塗る */
const DIM_COLOR = 'rgba(15, 23, 42, 0.62)';
/**
 * 通常のモーダル（TaskyDialog 等）より上、確認ダイアログ（z-[10000]）とトースト（z-[10001]）より下。
 * 固定ヘッダ（z-[1000]）には暗幕をかける。
 */
const Z_INDEX = 9990;

export interface TaskySpotlightProps {
  /** 強調する要素。null なら画面中央に説明カードだけ出す */
  target: Element | null;
  title: string;
  body: string;
  stepIndex: number;
  stepCount: number;
  onNext: () => void;
  onSkip: () => void;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const readRect = (element: Element): Rect => {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top - HIGHLIGHT_PADDING,
    left: rect.left - HIGHLIGHT_PADDING,
    width: rect.width + HIGHLIGHT_PADDING * 2,
    height: rect.height + HIGHLIGHT_PADDING * 2,
  };
};

/**
 * 画面を暗くして1つの要素だけを浮かび上がらせ、横に説明カードを出すガイド表示。
 * 背面の操作はクリックを吸収して止める（説明中に別操作で画面が変わらないように）。
 */
export const TaskySpotlight: React.FC<TaskySpotlightProps> = ({
  target,
  title,
  body,
  stepIndex,
  stepCount,
  onNext,
  onSkip,
}) => {
  const [rect, setRect] = useState<Rect | null>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const isLast = stepIndex >= stepCount - 1;

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom',
    strategy: 'fixed',
    // サイドバーのように縦に画面いっぱいの対象は上下に置けないので左右へ逃がし、
    // それでも収まらなければ画面内へ押し戻す（対象に重なっても操作できることを優先）
    middleware: [
      offset(12),
      flip({ padding: 16, fallbackPlacements: ['top', 'right', 'left'] }),
      shift({ padding: 16, crossAxis: true }),
    ],
    whileElementsMounted: autoUpdate,
  });

  // 対象を画面内へ寄せてから位置を測る
  useLayoutEffect(() => {
    if (!target) {
      setRect(null);
      return;
    }
    if (typeof (target as HTMLElement).scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'center', inline: 'nearest' });
    }
    setRect(readRect(target));
    refs.setReference(target);
  }, [target, refs]);

  // スクロール・リサイズ・レイアウト変化に追従する
  useEffect(() => {
    if (!target) return undefined;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setRect(readRect(target)));
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(target);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      observer?.disconnect();
    };
  }, [target]);

  useEffect(() => {
    nextButtonRef.current?.focus();
  }, [stepIndex]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onSkip();
      }
    },
    [onSkip],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const card = (
    <div
      ref={target ? refs.setFloating : undefined}
      style={target ? { ...floatingStyles, zIndex: Z_INDEX + 1 } : undefined}
      className={
        target
          ? 'w-80 max-w-[calc(100vw-2rem)] rounded-tasky-lg bg-white p-5 shadow-xl'
          : 'relative w-96 max-w-full rounded-tasky-lg bg-white p-6 shadow-xl'
      }
      role="dialog"
      aria-modal="true"
      aria-labelledby="tasky-spotlight-title"
      aria-describedby="tasky-spotlight-body"
      data-tutorial-root=""
    >
      <p className="text-xs font-medium text-primary">
        {stepIndex + 1} / {stepCount}
      </p>
      <h2 id="tasky-spotlight-title" className="mt-1 text-base font-bold text-dark">
        {title}
      </h2>
      <p id="tasky-spotlight-body" className="mt-2 text-sm leading-relaxed text-gray-600">
        {body}
      </p>
      <div className="mt-4 flex items-center justify-between gap-3">
        <TaskyButton type="button" variant="ghost" size="sm" onClick={onSkip}>
          スキップ
        </TaskyButton>
        <TaskyButton ref={nextButtonRef} type="button" size="sm" onClick={onNext}>
          {isLast ? '完了' : '次へ'}
        </TaskyButton>
      </div>
    </div>
  );

  return createPortal(
    <div data-tutorial-root="" data-testid="tasky-spotlight">
      {/* 背面のクリックを吸収する層。強調枠がない（中央カード）ときはここで暗くする */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: Z_INDEX,
          background: target ? 'transparent' : DIM_COLOR,
        }}
      />
      {target && rect && (
        <div
          aria-hidden="true"
          data-testid="tasky-spotlight-highlight"
          className="rounded-tasky-sm ring-2 ring-white/80 transition-all duration-200"
          style={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            zIndex: Z_INDEX,
            boxShadow: `0 0 0 9999px ${DIM_COLOR}`,
            pointerEvents: 'none',
          }}
        />
      )}
      {target ? (
        card
      ) : (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: Z_INDEX + 1 }}
          className="flex items-center justify-center p-4"
        >
          {card}
        </div>
      )}
    </div>,
    document.body,
  );
};

TaskySpotlight.displayName = 'TaskySpotlight';
